"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const AssertionError_1 = require("../error/AssertionError");
const EtudeError_1 = require("../error/EtudeError");
const Exceptional_1 = require("../util/Exceptional");
const MathUtil_1 = require("../util/MathUtil");
const Accidental_1 = require("./Accidental");
const Key_1 = require("./Key");
const Letter_1 = require("./Letter");
const Pitch_1 = require("./Pitch");
const Policy_1 = require("./Policy");
const Scale_1 = require("./Scale");
class KeySignature {
    constructor(accidentals) {
        this.accidentals = accidentals;
        if (accidentals
            .map(k => k.getAccidental())
            .some(a => a.map(a => a.getOffset()).orElse(0) == 0)) {
            throw EtudeError_1.default.forInvalid(KeySignature, accidentals, "null or Accidental.NATURAL");
        }
        else if (accidentals.length != new Set(accidentals.map(k => k.getLetter())).size) {
            throw EtudeError_1.default.forInvalid(KeySignature, accidentals, "all letters should be distinct");
        }
    }
    getAccidentalCount() {
        return this.accidentals.length;
    }
    getAccidentalFor(letter) {
        const value = this.accidentals
            .filter(k => k.getLetter() === letter)
            .map(k => k.getAccidental())[0];
        return value == null ? Exceptional_1.default.empty() : value;
    }
    static getOrderOfFlats(count = Letter_1.default.size) {
        return KeySignature.ORDER_OF_FLATS.slice(0, count);
    }
    static getOrderOfSharps(count = Letter_1.default.size) {
        return KeySignature.ORDER_OF_SHARPS.slice(0, count);
    }
    static getKeyFor(accidental, count, quality) {
        if (count < 0 || count > Letter_1.default.size) {
            return Exceptional_1.default.empty(EtudeError_1.default.forInvalid(KeySignature, count, "out of range"));
        }
        else if ((count === 0) != (accidental == null || accidental == Accidental_1.default.NATURAL)) {
            return Exceptional_1.default.empty(EtudeError_1.default.forIllegalArgument(KeySignature, accidental, "key signature with 0 accidentals should have null or Accidental.NATURAL"));
        }
        let key;
        let letter;
        // determine the key assuming quality is MAJOR
        switch (accidental == null ? Accidental_1.default.NATURAL : accidental) {
            case Accidental_1.default.FLAT:
                letter = KeySignature.ORDER_OF_FLATS[MathUtil_1.default.floorMod(count - 2, Letter_1.default.size)];
                key = new Key_1.default(letter, 
                // accidental; if flats for key signature contain the letter, make the key flat
                KeySignature.getOrderOfFlats(count).some(l => l === letter)
                    ? Accidental_1.default.FLAT
                    : null);
                break;
            case Accidental_1.default.SHARP:
                letter = KeySignature.ORDER_OF_SHARPS[MathUtil_1.default.floorMod(count + 1, Letter_1.default.size)];
                key = new Key_1.default(letter, 
                // accidental; if sharps for key signature contain the letter, make the key sharp
                KeySignature.getOrderOfSharps(count).some(l => l === letter)
                    ? Accidental_1.default.SHARP
                    : null);
                break;
            case Accidental_1.default.NATURAL:
                letter = Letter_1.default.C;
                key = new Key_1.default(letter);
                break;
            default:
                return Exceptional_1.default.empty(EtudeError_1.default.forIllegalArgument(KeySignature, accidental, "should use null, Accidental.NATURAL, Accidental.FLAT, or Accidental.SHARP for key signatures"));
        }
        if (quality === KeySignature.Quality.MINOR) {
            const policies = count === 0
                ? Policy_1.default.prioritize(Policy_1.default.NONE_OR_NATURAL)
                : Policy_1.default.prioritize(Policy_1.default.NONE_OR_NATURAL, accidental == Accidental_1.default.FLAT
                    ? Policy_1.default.FLAT
                    : Policy_1.default.SHARP);
            const optionalKey = key.step(KeySignature.RELATIVE_MAJOR_TO_MINOR_DISTANCE, policies);
            if (!optionalKey.isPresent()) {
                throw new AssertionError_1.default("Failed to get relative minor key");
            }
            key = optionalKey.get();
        }
        return Exceptional_1.default.of(key);
    }
    static fromAccidentals(accidental, count) {
        if (count < 0 || count > Letter_1.default.size) {
            return Exceptional_1.default.empty(EtudeError_1.default.forInvalid(KeySignature, count, "out of range"));
        }
        else if ((count === 0) != (accidental == null || accidental == Accidental_1.default.NATURAL)) {
            return Exceptional_1.default.empty(EtudeError_1.default.forIllegalArgument(KeySignature, accidental, "key signature with 0 accidentals should have null or Accidental.NATURAL"));
        }
        let letters;
        switch (accidental == null ? Accidental_1.default.NATURAL : accidental) {
            case Accidental_1.default.FLAT:
                letters = KeySignature.getOrderOfFlats(count);
                break;
            case Accidental_1.default.SHARP:
                letters = KeySignature.getOrderOfSharps(count);
                break;
            case Accidental_1.default.NATURAL:
                letters = new Letter_1.default[0];
                break;
            default:
                return Exceptional_1.default.empty(EtudeError_1.default.forIllegalArgument(KeySignature, accidental, "should use null, Accidental.NATURAL, Accidental.FLAT, or Accidental.SHARP for key signatures"));
        }
        const accidentals = letters
            .map(l => new Key_1.default(l, accidental));
        return Exceptional_1.default.of(new KeySignature(accidentals));
    }
    static fromKey(key, quality) {
        // arbitrary octave
        const accidentals = new Scale_1.default(new Pitch_1.default(key, 4), quality === KeySignature.Quality.MAJOR ? Scale_1.default.Quality.MAJOR : Scale_1.default.Quality.NATURAL_MINOR)
            .stream()
            .limit(Letter_1.default.size)
            .map(p => p.getKey())
            .filter(k => k.hasAccidental() && !k.isNatural());
        if (accidentals.length !== 0) {
            accidentals[0]
                .getAccidental()
                .filter(a => a === Accidental_1.default.FLAT || a === Accidental_1.default.SHARP)
                .map(a => {
                switch (a) {
                    case Accidental_1.default.FLAT: return KeySignature.ORDER_OF_FLATS;
                    case Accidental_1.default.SHARP: return KeySignature.ORDER_OF_SHARPS;
                    default: throw new AssertionError_1.default("Filter failed.");
                }
            })
                .ifPresent(ordered => accidentals.sort((a, b) => MathUtil_1.default.compare(ordered.indexOf(a.getLetter()), ordered.indexOf(b.getLetter()))));
        }
        return new KeySignature(accidentals);
    }
    toString() {
        return this.accidentals.toString();
    }
    getAccidentals() {
        return this.accidentals;
    }
}
KeySignature.RELATIVE_MAJOR_TO_MINOR_DISTANCE = -3;
KeySignature.ORDER_OF_FLATS = "BEADGCF".split("").map(Letter_1.default.fromChar).map(e => e.get());
KeySignature.ORDER_OF_SHARPS = "FCGDAEB".split("").map(Letter_1.default.fromChar).map(e => e.get());
(function (KeySignature) {
    class Quality {
        constructor() {
            ++Quality.size;
            Quality._values.push(this);
        }
        static values() {
            return Quality._values.slice();
        }
        ordinal() {
            return Quality._values.indexOf(this);
        }
        static valueOf(qualityString) {
            const quality = Quality[qualityString];
            if (quality instanceof Quality) {
                return quality;
            }
            throw EtudeError_1.default.forInvalid(Quality, qualityString);
        }
        toString() {
            return Object.keys(Quality).find(q => Quality[q] === this);
        }
    }
    Quality.size = 0;
    Quality._values = [];
    Quality.MAJOR = new Quality();
    Quality.MINOR = new Quality();
    KeySignature.Quality = Quality;
})(KeySignature || (KeySignature = {}));
exports.default = KeySignature;
